import { Box, Container, Paper, Skeleton, Stack } from '@mui/material';

/**
 * What a page looks like before its data arrives.
 *
 * Loading is never mistaken for empty (docs/04-ui-ux.md#rules-for-actions),
 * and a skeleton in the shape of the page keeps the layout from jumping when
 * the rows land — a centred spinner does neither.
 */

/** A list page's stand-in: a heading, a filter bar and six rows. The router's Suspense fallback. */
export function PageSkeleton() {
  return (
    <Container maxWidth="lg" sx={{ py: 3 }} aria-busy="true" aria-label="Loading" data-testid="page-skeleton">
      <Skeleton variant="text" width={180} sx={{ fontSize: '1.5rem', mb: 2 }} />
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1.5}>
          <Skeleton variant="rounded" height={40} sx={{ flex: 1 }} />
          <Skeleton variant="rounded" height={40} width={140} sx={{ display: { xs: 'none', sm: 'block' } }} />
          <Skeleton variant="rounded" height={40} width={140} sx={{ display: { xs: 'none', md: 'block' } }} />
        </Stack>
      </Paper>
      <TableSkeleton rows={6} columns={5} />
    </Container>
  );
}

/** Rows of a table before its data arrives; drop in where the table will be. */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-busy="true" aria-label="Loading" data-testid="table-skeleton">
      <Stack spacing={1.25}>
        {Array.from({ length: rows }, (_, r) => (
          <Box key={r} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {Array.from({ length: columns }, (_, c) => (
              <Skeleton
                key={c}
                variant="text"
                sx={{ flex: c === 0 ? 2 : 1, fontSize: '1rem', display: { xs: c > 1 ? 'none' : 'block', md: 'block' } }}
              />
            ))}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}
